import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { nomeDoMes } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { reais, type RecorrenciaDaLista } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { RecorrenciaDialog } from "./RecorrenciaDialog";

/** Aluguel, salários, sistemas: lançados uma vez, previstos todo mês em "Contas a pagar". */
export function CustosFixos() {
  const utils = trpc.useUtils();
  const pode = usePermissao("finance.manage");
  const lista = trpc.financeiro.recorrencias.listar.useQuery().data ?? [];
  const [dialogo, setDialogo] = useState<{ editando: RecorrenciaDaLista | null } | null>(null);
  const encerrar = trpc.financeiro.recorrencias.encerrar.useMutation({
    onSuccess: () => {
      toast.success("Custo fixo encerrado. Os meses seguintes saíram de Contas a pagar.");
      utils.financeiro.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const vigentes = lista.filter((r) => !r.encerrada);
  const totalMensal = vigentes.reduce((t, r) => t + r.valorCents, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-zinc-600 dark:text-muted-foreground">
          Custos fixos por mês: <strong className="tabular-nums text-zinc-900 dark:text-foreground">{reais(totalMensal)}</strong>
        </p>
        {pode && (
          <button
            type="button"
            onClick={() => setDialogo({ editando: null })}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" /> Novo custo fixo
          </button>
        )}
      </div>

      {lista.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 py-10 text-center text-sm text-zinc-500 dark:border-border dark:text-muted-foreground">
          Cadastre aluguel, salários, internet e o que mais se repete todo mês.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:divide-border/50 dark:border-border dark:bg-card">
          {lista.map((r) => (
            <li key={r.id} className={cn("flex flex-wrap items-center gap-3 px-4 py-3", r.encerrada && "opacity-60")}>
              <span className="h-3 w-3 shrink-0 rounded-[3px]" style={{ backgroundColor: r.categoria.cor || "#A1A1AA" }} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{r.descricao}</p>
                <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                  {r.categoria.nome} · vence todo dia {r.diaDoVencimento} · desde {nomeDoMes(r.inicio)}
                  {r.fim && ` · até ${nomeDoMes(r.fim)}`}
                  {r.partner && ` · ${r.partner.name}`}
                </p>
              </div>
              {r.encerrada && <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10.5px] font-bold uppercase text-zinc-500 dark:bg-muted">Encerrado</span>}
              <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-foreground">{reais(r.valorCents)}</span>
              {pode && (
                <div className="flex items-center gap-1">
                  <button type="button" aria-label={`Editar ${r.descricao}`} onClick={() => setDialogo({ editando: r })} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-muted">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  {!r.encerrada && !r.fim && (
                    <button type="button" onClick={() => encerrar.mutate({ id: r.id })} className="rounded-md px-2 py-1 text-xs font-semibold text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-muted">
                      Encerrar
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <RecorrenciaDialog aberto={!!dialogo} editando={dialogo?.editando ?? null} onFechar={() => setDialogo(null)} />
    </div>
  );
}
